import axios from 'axios'
import { User } from 'firebase/auth'

import {
	AgentAnalysisParams,
	CallListResponse,
	CallsListParams,
	TranscriptAnalysisParams,
	TranscriptAnalysisResult,
	TranscriptComparisonParams,
	UserAgentComparisonResult,
} from 'types/server/analysis'
import { CallInsight } from 'types/server/call-insights'

const API_URL = process.env.REACT_APP_API_URL

/**
 * Fetch transcript analysis data
 *
 * @param user - Firebase user for authentication
 * @param params - Query parameters for filtering and configuration
 * @returns Transcript analysis results
 */
// TODO: Replace hardcoded token with user.getIdToken() for future
const TEMP_AUTH_TOKEN = `Bearer ${process.env.REACT_APP_ANALYSIS_TOKEN}`

console.log('Using Analysis API URL:', process.env.REACT_APP_ANALYSIS_TOKEN)
export const fetchTranscriptAnalysis = async (
	_user: User,
	params: TranscriptAnalysisParams = {}
): Promise<TranscriptAnalysisResult> => {
	const {
		agentId,
		afterTimestamp,
		beforeTimestamp,
		days,
		limit,
		role,
		topWords,
		topPhrases,
		phraseLength,
		targetWords,
	} = params

	const response = await axios.get<TranscriptAnalysisResult>(`${API_URL}/analysis/transcripts`, {
		params: {
			agentId: agentId?.join(','),
			afterTimestamp,
			beforeTimestamp,
			days,
			limit,
			role,
			topWords,
			topPhrases,
			phraseLength,
			words: targetWords?.join(','),
		},
		headers: {
			Authorization: TEMP_AUTH_TOKEN,
		},
	})

	return response.data
}

/**
 * Fetch transcript comparison between users and agents
 *
 * @param user - Firebase user for authentication
 * @param params - Query parameters for filtering
 * @returns Comparison results for user and agent transcripts
 */
export const fetchTranscriptComparison = async (
	_user: User,
	params: TranscriptComparisonParams = {}
): Promise<UserAgentComparisonResult> => {
	const { agentId, afterTimestamp, beforeTimestamp, limit } = params

	const response = await axios.get<UserAgentComparisonResult>(
		`${API_URL}/analysis/transcripts/compare`,
		{
			params: {
				agentId: agentId?.join(','),
				afterTimestamp,
				beforeTimestamp,
				limit,
			},
			headers: {
				Authorization: TEMP_AUTH_TOKEN,
			},
		}
	)

	return response.data
}

/**
 * Fetch analysis for a specific agent
 *
 * @param user - Firebase user for authentication
 * @param agentId - The agent ID to analyse
 * @param params - Optional query parameters
 * @returns Transcript analysis for the specified agent
 */
export const fetchAgentAnalysis = async (
	_user: User,
	agentId: string,
	params: AgentAnalysisParams = {}
): Promise<TranscriptAnalysisResult> => {
	const { afterTimestamp, beforeTimestamp, topWords, topPhrases } = params

	const response = await axios.get<TranscriptAnalysisResult>(
		`${API_URL}/analysis/agent/${agentId}`,
		{
			params: {
				afterTimestamp,
				beforeTimestamp,
				topWords,
				topPhrases,
			},
			headers: {
				Authorization: TEMP_AUTH_TOKEN,
			},
		}
	)

	return response.data
}

/**
 * Fetch list of calls with optional filtering
 *
 * @param user - Firebase user for authentication
 * @param params - Query parameters for filtering
 * @returns List of calls
 */
export const fetchCallsList = async (
	_user: User,
	params: CallsListParams = {}
): Promise<CallListResponse> => {
	const { agentId, afterTimestamp, beforeTimestamp, limit } = params

	const response = await axios.get<CallListResponse>(`${API_URL}/analysis/calls`, {
		params: {
			agentId: agentId?.join(','),
			afterTimestamp,
			beforeTimestamp,
			limit,
		},
		headers: {
			Authorization: TEMP_AUTH_TOKEN,
		},
	})

	return response.data
}

/**
 * Fetch a specific call by ID
 *
 * @param user - Firebase user for authentication
 * @param callId - The call ID to retrieve
 * @returns Call details
 */
export const fetchCallById = async (_user: User, callId: string): Promise<CallInsight> => {
	const response = await axios.get<CallInsight>(`${API_URL}/analysis/call/${callId}`, {
		headers: {
			Authorization: TEMP_AUTH_TOKEN,
		},
	})

	return response.data
}
export type {
	TranscriptAnalysisParams,
	AgentAnalysisParams,
	CallsListParams,
	TranscriptComparisonParams,
}
